/**
 * Site Geofence Map Component
 * Interactive Leaflet + OpenStreetMap map with circle/polygon boundary editing
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  makeStyles,
  Button,
  Text,
  Label,
  Input,
  RadioGroup,
  Radio,
  Badge,
} from '@fluentui/react-components';
import {
  SaveRegular,
  DismissRegular,
  DeleteRegular,
  MyLocationRegular,
} from '@fluentui/react-icons';
import L from 'leaflet';
import 'leaflet-draw';

// Fix Leaflet default marker icon issue with bundlers
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const useStyles = makeStyles({
  container: {
    marginTop: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
  },
  mapWrapper: {
    width: '100%',
    height: '450px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e1dfdd',
    position: 'relative',
  },
  configPanel: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '280px',
    backgroundColor: 'rgba(255, 255, 255, 0.97)',
    borderRadius: '8px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
    zIndex: 1000,
    overflow: 'hidden',
  },
  configHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  configBody: {
    padding: '12px 16px',
  },
  configFooter: {
    padding: '10px 16px',
    borderTop: '1px solid #e1dfdd',
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  formField: {
    marginBottom: '12px',
  },
  radiusControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  radiusValue: {
    fontSize: '13px',
    fontWeight: '600',
    minWidth: '50px',
    textAlign: 'right' as const,
  },
  statusBadge: {
    position: 'absolute' as const,
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
  },
  polygonInstructions: {
    padding: '10px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#605e5c',
    lineHeight: '1.6',
  },
});

interface LatLng {
  lat: number;
  lng: number;
}

interface SiteGeofenceMapProps {
  latitude?: number | string | null;
  longitude?: number | string | null;
  radiusMeters?: number;
  boundaryType?: 'circle' | 'polygon';
  boundaryPolygon?: LatLng[] | null;
  siteName?: string;
  readOnly?: boolean;
  onSave?: (data: {
    boundary_type: 'circle' | 'polygon';
    radius_meters: number;
    boundary_polygon: LatLng[] | null;
  }) => void;
}

const SiteGeofenceMap: React.FC<SiteGeofenceMapProps> = ({
  latitude,
  longitude,
  radiusMeters = 100,
  boundaryType = 'circle',
  boundaryPolygon = null,
  siteName = 'Site',
  readOnly = false,
  onSave,
}) => {
  const styles = useStyles();
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const polygonRef = useRef<L.Polygon | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const drawControlRef = useRef<L.Control.Draw | null>(null);

  const [currentType, setCurrentType] = useState<'circle' | 'polygon'>(boundaryType);
  const [radius, setRadius] = useState(radiusMeters);
  const [polygonPoints, setPolygonPoints] = useState<LatLng[]>(boundaryPolygon || []);
  const [configOpen, setConfigOpen] = useState(false);

  // Parse coordinates - MySQL decimal columns return as strings
  const parsedLat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
  const parsedLng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;

  const hasCoords =
    typeof parsedLat === 'number' &&
    typeof parsedLng === 'number' &&
    !isNaN(parsedLat) &&
    !isNaN(parsedLng);

  // Show circle boundary
  const showCircle = useCallback((map: L.Map, lat: number, lng: number, r: number) => {
    if (circleRef.current) {
      map.removeLayer(circleRef.current);
    }
    circleRef.current = L.circle([lat, lng], {
      radius: r,
      color: '#0078d4',
      fillColor: '#0078d4',
      fillOpacity: 0.12,
      weight: 2,
      dashArray: '6 4',
    }).addTo(map);
  }, []);

  // Show polygon boundary
  const showPolygon = useCallback((map: L.Map, points: LatLng[]) => {
    if (polygonRef.current) {
      map.removeLayer(polygonRef.current);
    }
    const latlngs = points.map(p => [p.lat, p.lng] as L.LatLngExpression);
    polygonRef.current = L.polygon(latlngs, {
      color: '#2563eb',
      fillColor: '#2563eb',
      fillOpacity: 0.12,
      weight: 2,
      dashArray: '6 4',
    }).addTo(map);
  }, []);

  // Remove draw control
  const removeDrawControl = useCallback(() => {
    if (drawControlRef.current && mapRef.current) {
      mapRef.current.removeControl(drawControlRef.current);
      drawControlRef.current = null;
    }
  }, []);

  // Add Leaflet Draw control for polygon drawing
  const addDrawControl = useCallback(
    (map: L.Map) => {
      removeDrawControl();

      const drawControl = new L.Control.Draw({
        position: 'topleft',
        draw: {
          polygon: {
            shapeOptions: {
              color: '#2563eb',
              fillColor: '#2563eb',
              fillOpacity: 0.15,
              weight: 3,
            },
            allowIntersection: false,
            showArea: true,
          },
          polyline: false,
          rectangle: false,
          circle: false,
          marker: false,
          circlemarker: false,
        },
        edit: {
          featureGroup: drawnItemsRef.current,
        },
      });

      map.addControl(drawControl);
      drawControlRef.current = drawControl;

      // Handle polygon creation
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.on(L.Draw.Event.CREATED, (e: any) => {
        drawnItemsRef.current.clearLayers();
        if (polygonRef.current) {
          map.removeLayer(polygonRef.current);
          polygonRef.current = null;
        }

        const layer = e.layer;
        drawnItemsRef.current.addLayer(layer);

        const coords: LatLng[] = layer.getLatLngs()[0].map((ll: L.LatLng) => ({
          lat: ll.lat,
          lng: ll.lng,
        }));
        setPolygonPoints(coords);
      });

      // Handle polygon edit
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.on(L.Draw.Event.EDITED, (e: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        e.layers.eachLayer((layer: any) => {
          const coords: LatLng[] = layer.getLatLngs()[0].map((ll: L.LatLng) => ({
            lat: ll.lat,
            lng: ll.lng,
          }));
          setPolygonPoints(coords);
        });
      });

      // Handle polygon delete
      map.on(L.Draw.Event.DELETED, () => {
        setPolygonPoints([]);
      });
    },
    [removeDrawControl]
  );

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return;
    }

    const center: L.LatLngExpression = hasCoords ? [parsedLat!, parsedLng!] : [7.5, -1.5];

    const zoom = hasCoords ? 15 : 7;

    const map = L.map(mapContainerRef.current, {
      center,
      zoom,
      zoomControl: false,
    });

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    // OpenStreetMap tiles - FREE, no API key needed
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    // Add marker at site location
    if (hasCoords) {
      L.marker([parsedLat!, parsedLng!])
        .addTo(map)
        .bindPopup(`<strong>${siteName}</strong><br/>Lat: ${parsedLat}, Lng: ${parsedLng}`)
        .openPopup();
    }

    // Add drawn items layer
    drawnItemsRef.current.addTo(map);

    mapRef.current = map;

    // Show existing boundary
    if (hasCoords) {
      if (boundaryType === 'circle' && radiusMeters) {
        showCircle(map, parsedLat!, parsedLng!, radiusMeters);
      } else if (boundaryType === 'polygon' && boundaryPolygon && boundaryPolygon.length >= 3) {
        showPolygon(map, boundaryPolygon);
      }
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update circle when radius changes
  useEffect(() => {
    if (!mapRef.current || !hasCoords || currentType !== 'circle') {
      return;
    }
    showCircle(mapRef.current, parsedLat!, parsedLng!, radius);
  }, [radius, currentType, hasCoords, parsedLat, parsedLng, showCircle]);

  // Handle boundary type change
  const handleTypeChange = (type: 'circle' | 'polygon') => {
    setCurrentType(type);
    const map = mapRef.current;
    if (!map) {
      return;
    }

    // Clear existing boundaries
    if (circleRef.current) {
      map.removeLayer(circleRef.current);
      circleRef.current = null;
    }
    if (polygonRef.current) {
      map.removeLayer(polygonRef.current);
      polygonRef.current = null;
    }
    drawnItemsRef.current.clearLayers();
    removeDrawControl();

    if (type === 'circle' && hasCoords) {
      showCircle(map, parsedLat!, parsedLng!, radius);
    } else if (type === 'polygon') {
      addDrawControl(map);
      if (polygonPoints.length >= 3) {
        showPolygon(map, polygonPoints);
      }
    }
  };

  // Save handler
  const handleSave = () => {
    if (!onSave) {
      return;
    }

    if (currentType === 'polygon' && polygonPoints.length < 3) {
      return; // Validation handled in parent
    }

    onSave({
      boundary_type: currentType,
      radius_meters: currentType === 'circle' ? radius : 0,
      boundary_polygon: currentType === 'polygon' ? polygonPoints : null,
    });
  };

  // Clear boundary
  const handleClear = () => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    if (circleRef.current) {
      map.removeLayer(circleRef.current);
      circleRef.current = null;
    }
    if (polygonRef.current) {
      map.removeLayer(polygonRef.current);
      polygonRef.current = null;
    }
    drawnItemsRef.current.clearLayers();
    setPolygonPoints([]);
    setRadius(100);
  };

  if (!hasCoords) {
    return (
      <div className={styles.container}>
        <Text className={styles.sectionTitle}>Geofence Boundary</Text>
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#605e5c',
            border: '1px dashed #e1dfdd',
            borderRadius: '8px',
            marginTop: '12px',
          }}
        >
          <MyLocationRegular style={{ fontSize: '36px', marginBottom: '8px' }} />
          <Text style={{ display: 'block' }} weight="semibold">
            No GPS coordinates available
          </Text>
          <Text size={200} style={{ display: 'block', marginTop: '4px' }}>
            Set the site&apos;s latitude and longitude first to configure geofencing
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <Text className={styles.sectionTitle}>Geofence Boundary</Text>
          <Text size={200} style={{ color: '#605e5c', display: 'block', marginTop: '2px' }}>
            {parsedLat?.toFixed(6)}, {parsedLng?.toFixed(6)}
            {currentType === 'circle' && ` • ${radius}m radius`}
            {currentType === 'polygon' &&
              polygonPoints.length > 0 &&
              ` • ${polygonPoints.length}-point polygon`}
          </Text>
        </div>
        {!readOnly && (
          <Button appearance="subtle" size="small" onClick={() => setConfigOpen(!configOpen)}>
            {configOpen ? 'Hide Config' : 'Configure Boundary'}
          </Button>
        )}
      </div>

      <div className={styles.mapWrapper}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

        {/* Config Panel */}
        {configOpen && !readOnly && (
          <div className={styles.configPanel}>
            <div className={styles.configHeader}>
              <Text weight="semibold" size={300}>
                Boundary Settings
              </Text>
              <Button
                appearance="subtle"
                size="small"
                icon={<DismissRegular />}
                onClick={() => setConfigOpen(false)}
              />
            </div>

            <div className={styles.configBody}>
              {/* Boundary Type */}
              <div className={styles.formField}>
                <Label
                  size="small"
                  style={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#605e5c',
                  }}
                >
                  Boundary Type
                </Label>
                <RadioGroup
                  layout="horizontal"
                  value={currentType}
                  onChange={(_, data) => handleTypeChange(data.value as 'circle' | 'polygon')}
                >
                  <Radio value="circle" label="Radius" />
                  <Radio value="polygon" label="Polygon" />
                </RadioGroup>
              </div>

              {/* Circle Config */}
              {currentType === 'circle' && (
                <div className={styles.formField}>
                  <Label
                    size="small"
                    style={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '11px',
                      color: '#605e5c',
                    }}
                  >
                    Radius (meters)
                  </Label>
                  <div className={styles.radiusControl}>
                    <input
                      type="range"
                      min={20}
                      max={1000}
                      value={Math.min(radius, 1000)}
                      onChange={e => setRadius(parseInt(e.target.value))}
                      style={{ flex: 1, accentColor: '#0078d4' }}
                    />
                    <span className={styles.radiusValue}>{radius}m</span>
                  </div>
                  <Input
                    type="number"
                    value={String(radius)}
                    min={20}
                    max={5000}
                    onChange={e =>
                      setRadius(Math.max(20, Math.min(5000, parseInt(e.target.value) || 100)))
                    }
                    size="small"
                    style={{ marginTop: '6px' }}
                  />
                </div>
              )}

              {/* Polygon Config */}
              {currentType === 'polygon' && (
                <div className={styles.polygonInstructions}>
                  <strong style={{ color: '#323130' }}>Draw a polygon boundary:</strong>
                  <br />
                  1. Click the polygon icon on the left toolbar
                  <br />
                  2. Click points on the map to define the boundary
                  <br />
                  3. Click the first point to close the shape
                  <br />
                  <div
                    style={{
                      marginTop: '8px',
                      fontWeight: 600,
                      color: polygonPoints.length >= 3 ? '#0e7a0d' : '#d97706',
                    }}
                  >
                    {polygonPoints.length >= 3
                      ? `✓ Polygon with ${polygonPoints.length} points defined`
                      : 'No polygon drawn yet'}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.configFooter}>
              <Button
                size="small"
                appearance="subtle"
                icon={<DeleteRegular />}
                onClick={handleClear}
              >
                Clear
              </Button>
              <Button
                size="small"
                appearance="primary"
                icon={<SaveRegular />}
                onClick={handleSave}
                disabled={currentType === 'polygon' && polygonPoints.length < 3}
              >
                Save Boundary
              </Button>
            </div>
          </div>
        )}

        {/* Status badge */}
        {currentType === 'circle' && (
          <div className={styles.statusBadge}>
            <Badge appearance="filled" color="brand" shape="rounded">
              {radius}m radius geofence
            </Badge>
          </div>
        )}
        {currentType === 'polygon' && polygonPoints.length >= 3 && (
          <div className={styles.statusBadge}>
            <Badge appearance="filled" color="informative" shape="rounded">
              {polygonPoints.length}-point polygon geofence
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteGeofenceMap;
